import { captureException, captureMessage, withScope } from "@sentry/react";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { useBasketRefreshOnError } from "@/hooks/useBasketRefreshOnError";
import { useHandleError } from "@/hooks/useHandleError";
import { transformPaymentMean } from "@/hooks/usePaymentMode";
import { FullScreenLoadingContext } from "@/provider/FullScreenLoadingProvider/FullScreenLoadingProvider";
import {
  useAddOptionToContract,
  useCreateTransaction,
  usePayTransaction,
} from "@/services/api";
import {
  Basket,
  CreateTransactionDatatransOrderType,
  DatatransPaymentResponse,
  DatatransPaymentResponseStatus,
} from "@/services/model";
import { getHeader } from "@/utils/basketUtils";
import { get3DsBrowserInfo } from "@/utils/lightboxHelpers";

/** Sentry `domain` tag: the screen a checkout report comes from. */
export type OptionCheckoutDomain =
  "CheckoutRoamingOption" | "AddWatchSimOption" | "SubscriptionCheckout";

interface Params {
  domain: OptionCheckoutDomain;
  simId: string;
  basket: Basket | null | undefined;
  /** Rebuilds the basket after a recoverable basket error. */
  refreshBasket: () => void;
  /**
   * The payment method the checkout charges: the saved one, or for a tariff
   * change the one the order response carries.
   */
  paymentMode: Pick<
    ReturnType<typeof transformPaymentMean>,
    "isInvoice" | "isCreditCard" | "maskedCardNumberString" | "cardType"
  >;
  onSuccess: () => void;
  /**
   * Tariff change only: a saved card with nothing to charge adds the option
   * directly. Roaming and Watch SIM always run the card transaction.
   */
  skipCardPaymentWhenFree?: boolean;
}

/**
 * Submits an option basket on an existing SIM (roaming package, Watch SIM,
 * tariff change): saved card → pay, invoice → add the option directly, no
 * payment method → open the Datatrans Lightbox.
 */
export const useOptionCheckoutSubmit = ({
  domain,
  simId,
  basket,
  refreshBasket,
  paymentMode,
  onSuccess,
  skipCardPaymentWhenFree = false,
}: Params) => {
  const { t } = useTranslation();
  const handleError = useHandleError();
  const { setFullScreenLoading } = useContext(FullScreenLoadingContext);
  const { isInvoice, isCreditCard, maskedCardNumberString, cardType } =
    paymentMode;
  const [isSubmittingCheckout, setIsSubmittingCheckout] = useState(false);
  const handleRefreshableBasketError = useBasketRefreshOnError({
    basketId: basket?.id,
    refreshBasket,
  });

  const report = (capture: () => void, basketErrorRecovered?: boolean) =>
    withScope((scope) => {
      scope.setFingerprint(["/contract/serviceId/options"]);
      scope.setTag("section", "portal");
      scope.setTag("domain", domain);
      scope.setTag("endpoint", "addOptionToContract");
      if (basketErrorRecovered !== undefined) {
        scope.setTag("basketErrorRecovered", basketErrorRecovered);
      }
      scope.setExtra("simId", simId);
      scope.setExtra("basket", basket);
      scope.setContext("paymentContext", {
        isInvoice,
        isCreditCard,
        maskedCardNumberString,
        cardType,
      });
      capture();
    });

  const handleTransactionError = (err: unknown) => {
    setIsSubmittingCheckout(false);
    setFullScreenLoading(false);

    const isRecoveredBasketError = handleRefreshableBasketError(err);

    if (!isRecoveredBasketError) {
      handleError(err);
    }

    report(() => captureException(err), isRecoveredBasketError);
  };

  // Only the card payment carries a status; a successful add-option call
  // (invoice, nothing to charge) means the option is booked.
  const handlePaymentResult = (data: DatatransPaymentResponse) => {
    setFullScreenLoading(false);

    if (data.status === DatatransPaymentResponseStatus.OK) {
      onSuccess();

      return;
    }

    setIsSubmittingCheckout(false);
    handleError(
      "Payment failure",
      `${t("Error.payment.fail")} ${t("Error.payment.pleaseCheckData")}`,
    );
    report(() => captureMessage("Payment failure"));
  };

  const handleOptionAdded = () => {
    setFullScreenLoading(false);
    onSuccess();
  };

  const {
    mutate: createTransaction,
    data: datatransRequestDocument,
    isPending: isCreatingTransaction,
  } = useCreateTransaction({
    mutation: {
      onError: (err) => {
        setFullScreenLoading(false);

        if (!handleRefreshableBasketError(err)) {
          handleError(err);
        }
      },
    },
  });

  const { mutate: payTransaction } = usePayTransaction({
    mutation: {
      onSuccess: handlePaymentResult,
      onError: handleTransactionError,
    },
  });

  const {
    mutate: addOptionToContract,
    isPending: isAddingOptionLoading,
    isSuccess: isAddingOptionDone,
  } = useAddOptionToContract({
    mutation: { onSuccess: handleOptionAdded, onError: handleTransactionError },
  });

  const submit = (totalOnceAmount: number | undefined) => {
    const basketId = basket?.id;

    if (basketId === undefined) {
      captureMessage("Wanted to submit undefined basket id!");
      handleError("Wanted to submit undefined basket id!");

      return;
    }
    if (totalOnceAmount === undefined) {
      captureMessage("Wanted to charge undefined oneTime amount");
      handleError("Wanted to charge undefined oneTime amount");

      return;
    }
    if (isSubmittingCheckout) {
      return;
    }

    const transactionParams = {
      simId: parseInt(simId),
      basketId,
      datatransOrderType:
        CreateTransactionDatatransOrderType.AddOptionToContract,
    };

    // No saved payment method: the Lightbox collects it and adds the option
    // backend-side. No loading overlay here - the Lightbox would sit under it.
    if (!isInvoice && !isCreditCard) {
      createTransaction({
        params: { ...transactionParams, ...get3DsBrowserInfo() },
      });

      return;
    }

    setIsSubmittingCheckout(true); // Prevents a double checkout on re-render
    setFullScreenLoading(true, t("Onboarding.sections.checkout.loading"));

    if (isCreditCard && !(skipCardPaymentWhenFree && totalOnceAmount <= 0)) {
      payTransaction({ params: transactionParams });

      return;
    }

    addOptionToContract({
      serviceId: Number(simId),
      data: { header: getHeader(), basket: basket ?? undefined, basketId },
    });
  };

  return {
    submit,
    isSubmittingCheckout,
    setIsSubmittingCheckout,
    datatransRequestDocument,
    isCreatingTransaction,
    isAddingOptionLoading,
    isAddingOptionDone,
  };
};
